/* eslint-disable no-use-before-define */
type Attributes = Record<string, string | number | boolean | null | undefined>;

type RelationshipData = {
  id: string;
  type?: string;
};

type Relationship = {
  data: Array<RelationshipData> | RelationshipData;
};

type RecordData = {
  id: string;
  type: string;
  attributes: Attributes;
  relationships?: Record<string, Relationship>;
};

type Json = {
  included?: Array<RecordData>;
  data: Array<RecordData> | RecordData;
  errors?: Array<any>;
  meta?: any;
};

const objectShaping = (obj: Record<string, unknown>): Record<string, unknown> => Object.keys(obj).reduce<Record<string, unknown>>((ccObj, field) => {
  let value: unknown;
  const currentValue = obj[field];

  if (Array.isArray(currentValue)) {
    value = currentValue.map((val: unknown) => {
      if (val && typeof val === 'object') {
        return objectShaping(val as Record<string, unknown>);
      }
      return val;
    });
  } else if (currentValue && typeof currentValue === 'object') {
    value = objectShaping(currentValue as Record<string, unknown>);
  } else {
    value = currentValue;
  }

  return {
    ...ccObj,
    [field]: Number.isInteger(value) ? (value as number).toString() : value,
  };
}, {});

const extractsOneRelationship = (
  json: Json,
  oneRelationship: RelationshipData,
): RelationshipData => {
  const element = json.included?.find(
    (item) => oneRelationship
      && item.type === oneRelationship.type
      && item.id === oneRelationship.id,
  );

  if (element) {
    const relationshipRecord: RelationshipData = {
      id: element.id,
      ...objectShaping(element.attributes),
    };
    addRelationship(element, json, relationshipRecord);
    return relationshipRecord;
  }
  return oneRelationship;
};

const addRelationship = (
  recordData: RecordData,
  json: Json,
  record: Record<string, unknown>,
): Record<string, unknown> => {
  if (recordData.relationships && json.included) {
    for (const name in recordData.relationships) {
      if (
        Object.prototype.hasOwnProperty.call(recordData.relationships, name)
      ) {
        const relationship = recordData.relationships[name];
        if (Array.isArray(relationship.data)) {
          record[name] = relationship.data.map((oneRelation) => extractsOneRelationship(json, oneRelation));
        } else {
          record[name] = extractsOneRelationship(json, relationship.data);
        }
      }
    }
  }
  return record;
};

const formatResponse = (json: any): any => {
  if (Array.isArray(json.errors)) {
    return json.errors;
  }
  if (Array.isArray(json.data)) {
    const result = json.data.map((record: any) => {
      const compiledObj = {
        id: record.id,
        ...objectShaping(record.attributes),
      };
      addRelationship(record, json, compiledObj);
      return compiledObj;
    });
    return { result, meta: json?.meta };
  }
  const result = {
    id: json.data.id,
    ...objectShaping(json.data.attributes),
  };
  addRelationship(json.data, json, result);
  return { result, meta: json?.meta };
};

export default formatResponse;
